import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { LogoMark } from "./Logo";

const LINKS = [
    { to: "/", label: "Home", id: "nav-link-home" },
    { to: "/services", label: "Services", id: "nav-link-services" },
    { to: "/portfolio", label: "Portfolio", id: "nav-link-portfolio" },
    { to: "/builders", label: "Contractors", id: "nav-link-builders" },
    { to: "/about", label: "About", id: "nav-link-about" },
    { to: "/contact", label: "Contact", id: "nav-link-contact" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => setOpen(false), [pathname]);

    return (
        <header className="fixed inset-x-0 top-0 z-50 border-b border-gold/20 bg-[#0B0F17]/85 backdrop-blur-xl">
            <nav className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4 sm:px-8 lg:px-16">
                <Link to="/" data-testid="nav-logo-link" className="shrink-0">
                    <span className="inline-flex items-center gap-3">
                        <LogoMark />
                        <span className="leading-none">
                            <span className="font-display block text-lg font-extrabold tracking-tight text-slate-50">
                                B&amp;B <span className="text-gold">Painting</span>
                            </span>
                            <span className="mt-1 block font-mono text-[10px] uppercase tracking-[0.3em] text-slate-400">
                                Renovation · Toronto
                            </span>
                        </span>
                    </span>
                </Link>
                <div className="hidden items-center gap-5 xl:flex">
                    {LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={l.id}
                            className={({ isActive }) =>
                                `whitespace-nowrap font-mono text-xs uppercase tracking-[0.16em] transition-colors ${
                                    isActive ? "text-gold" : "text-slate-300 hover:text-slate-50"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </div>
                <div className="flex items-center gap-3">
                    <a
                        href="tel:+16475104012"
                        data-testid="nav-phone-button"
                        className="hidden items-center gap-2 whitespace-nowrap border border-white/15 px-4 py-2.5 font-mono text-xs tracking-wider text-slate-100 transition-colors hover:border-gold hover:text-gold xl:inline-flex"
                    >
                        <Phone size={14} /> 647 510 4012
                    </a>
                    <Link
                        to="/contact"
                        data-testid="nav-quote-cta"
                        className="hidden whitespace-nowrap bg-gold px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wide text-ink transition-transform hover:-translate-y-0.5 md:block"
                    >
                        Request a Quote
                    </Link>
                    <button
                        data-testid="nav-mobile-toggle"
                        onClick={() => setOpen((v) => !v)}
                        className="border border-white/15 p-2.5 text-slate-100 xl:hidden"
                        aria-label="Toggle menu"
                    >
                        {open ? <X size={18} /> : <Menu size={18} />}
                    </button>
                </div>
            </nav>
            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="nav-mobile-panel"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="overflow-hidden border-t border-gold/20 bg-[#131924] lg:hidden"
                    >
                        <div className="flex flex-col gap-1 px-6 py-4">
                            {LINKS.map((l) => (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    data-testid={`${l.id}-mobile`}
                                    className={({ isActive }) =>
                                        `py-2.5 font-display text-lg font-bold ${isActive ? "text-gold" : "text-slate-100"}`
                                    }
                                >
                                    {l.label}
                                </NavLink>
                            ))}
                            <a
                                href="tel:+16475104012"
                                data-testid="nav-phone-button-mobile"
                                className="mt-2 flex items-center gap-2 border border-white/15 px-4 py-3 font-mono text-sm text-gold"
                            >
                                <Phone size={15} /> 647 510 4012
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}